import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Popconfirm, Progress, Row } from 'antd';
import { Message } from 'api/MessagesApi';
import { useMessageContext } from 'components/Message/CreateMessageComponent/MessageContext';
import ContentPreviewItem from 'components/Message/CreateMessageComponent/StepMessage/ContentPreview/ContentPreviewItem';
import { useMessageService } from 'components/Message/useMessageService';
import { displayDeliveryDate, logging } from 'utils/utils';
import './ContentPreview.css';

type AllProps = {
  message: Message;
  file?: File | null;
  text?: string;
  title?: string;
  onNext: (shouldExit?: boolean) => void;
  onBack: () => void;
  onRestart: () => void;
  goToDeliveryInfo: () => void;
};

function ContentPreview({ message, file, text, title, onNext, onBack, onRestart, goToDeliveryInfo }: AllProps) {
  const { updateMessageContent, uploadFile, progressEvent, abortUploading, updateIsLoading, uploadIsLoading } =
    useMessageService();
  const { setContent, setFile } = useMessageContext();

  const handleRestart = async () => {
    await updateMessageContent({ content: '', fileName: '' });
    setContent(null);
    setFile(null);
    onRestart();
  };

  const handleSubmit = async (shouldExit = false) => {
    if (file) {
      await uploadFile(file);
      setFile(null);
    }

    try {
      await updateMessageContent({ content: text, name: title });
      setContent(null);

      onNext(shouldExit);
    } catch (e) {
      logging(e);
    }
  };

  const isLoading = updateIsLoading || uploadIsLoading;

  const deliveryDate = displayDeliveryDate(message, '');

  if (!message) return null;

  return (
    <div className="step-message-preview create-message-component-full-height">
      <Flex vertical className="block-list" gap={20}>
        <div>
          <div className="create-message-component-title">Review your message information.</div>
          <div>
            Are you ready to complete? Please verify the information below. You will be able to edit or delete a
            completed message at any time.
          </div>
        </div>

        <div className="block content-data">
          <div className="title">
            <strong>Message Info</strong>
          </div>
          <ContentPreviewItem message={message} file={file} text={text} />

          <div className="text-center">
            <Popconfirm
              title="Start over?"
              description="You will lose all your progress with this message"
              onConfirm={handleRestart}
              okText="Start over"
              cancelText="Cancel"
            >
              <Button type="link">Remove and start again</Button>
            </Popconfirm>
          </div>
        </div>

        {progressEvent && (
          <>
            <div>Uploading...</div>
            <Flex align="center" className="uploading-progress" justify="space-between">
              <Progress percent={Math.round((progressEvent.progress || 0.5) * 100)}></Progress>
              <Button onClick={abortUploading} type="text">
                Cancel
              </Button>
            </Flex>
          </>
        )}

        <div className="block delivery-data">
          <div className="title">
            <strong>Delivery Info</strong>{' '}
            <span>
              <Button type="link" onClick={goToDeliveryInfo} style={{ padding: 0, height: 'auto' }}>
                edit
              </Button>
            </span>
          </div>

          <div className="content">
            <Row gutter={[0, 10]}>
              <Col span={24}>
                <strong>Deliver on:</strong> {deliveryDate}
              </Col>
              {(message.contacts || []).map((contact) => (
                <Col span={24} key={contact.id}>
                  <strong>Recipient:</strong> {contact?.name} ({contact?.email})
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </Flex>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onBack} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>
        <Col span={12} className="text-right">
          <Flex gap={20} justify="flex-end">
            <Button onClick={() => handleSubmit(true)} loading={isLoading}>
              Save & Exit
            </Button>
            <Button onClick={() => handleSubmit()} type="primary" loading={isLoading}>
              Next
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
}

export default ContentPreview;
